import { Button, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import Joyride, { CallBackProps, STATUS, Step, TooltipRenderProps } from 'react-joyride'
import { colors as variablesColors } from 'styles/variables'
import ContainedIconButton from 'ui/components/ContainedIconButton/ContainedIconButton'

type TourProps = {
  run: boolean
  setRunState: (arg0: boolean) => void
  steps: Array<Step>
  isSingle?: boolean
  dialogWidth?: number
}

function Tour({ run, setRunState, steps, isSingle, dialogWidth }: TourProps): ReactElement {
  const finalSteps = steps.map(({ target, ...rest }) => ({
    target: `.` + target,
    disableBeacon: true,
    ...rest,
  }))

  function handleJoyrideCallback(data: CallBackProps): void {
    const { status } = data
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

    if (finishedStatuses.includes(status)) {
      setRunState(false)
    }
  }

  const CustomJoyRideTooltip = ({
    continuous,
    index,
    isLastStep,
    step,
    backProps,
    primaryProps,
    skipProps,
    tooltipProps,
  }: TooltipRenderProps): React.JSX.Element => {
    const { t } = useTranslation()

    return (
      <Grid
        container
        {...tooltipProps}
        style={{ background: `white`, width: dialogWidth ? dialogWidth : 700, padding: 20, borderRadius: 10 }}
      >
        <Grid size={{ xs: 12 }}>
          {step.title && (
            <Typography variant="h1" display="block">
              {t(`${step.title}`)}
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12 }}>
          {step.content && (
            <Typography style={{ whiteSpace: `pre-line`, marginTop: 10 }}>
              {typeof step.content === 'string' ? t(`${step.content}`) : step.content}
            </Typography>
          )}
        </Grid>
        <Grid container direction="row" justifyContent="space-between" style={{ marginTop: 10 }}>
          <Grid>
            {!isLastStep && (
              <Button {...skipProps}>
                <Typography id="cancel">{t(`Cancel`)}</Typography>
              </Button>
            )}
          </Grid>
          <Grid>
            {index > 0 && (
              <ContainedIconButton
                icon="fal fa-angle-left"
                label={t(`Back`)}
                color="primary"
                {...backProps}
                style={{ marginRight: 10 }}
              />
            )}
            {continuous && !isLastStep && (
              <ContainedIconButton
                icon="fal fa-angle-right"
                color="primary"
                label={t(`Next`)}
                rightIcon
                {...primaryProps}
              />
            )}
            {isLastStep && (
              <ContainedIconButton icon="fal fa-times" color="secondary" label={t(`Close`)} {...primaryProps} />
            )}
          </Grid>
        </Grid>
      </Grid>
    )
  }
  const { t } = useTranslation()

  return (
    <>
      {run && (
        <Joyride
          tooltipComponent={CustomJoyRideTooltip}
          callback={handleJoyrideCallback}
          continuous={isSingle ? false : true}
          run={run}
          scrollToFirstStep={true}
          showProgress
          showSkipButton
          disableScrolling={true}
          disableOverlayClose={false}
          steps={finalSteps}
          styles={{
            options: {
              zIndex: 10000,
              arrowColor: variablesColors.white,
              backgroundColor: variablesColors.white,
              overlayColor: `rgba(0,0,0,0.4)`,
              primaryColor: variablesColors.primaryMainHover,
              textColor: variablesColors.black,
              width: 900,
            },
            buttonClose: {
              display: `none`,
            },
          }}
          locale={{
            back: t(`Back`),
            close: t(`Close`),
            last: t(`Close`),
            next: t(`Next`),
            skip: t(`Cancel`),
          }}
        />
      )}
    </>
  )
}

export default Tour
